import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { CrashModel } from '../../model/crash/CrashModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface CrashHudCallbacks {
  /** Tap on the action button while in idle state — start a new round. */
  onPlay: () => void;
  /** Tap on the action button while in rising state — lock the multiplier. */
  onCashOut: () => void;
}

/**
 * Crash HUD: balance, action button (toggles PLAY ↔ CASH OUT based on
 * round status), status label below.
 *
 * <p>The action button's label tracks the model's multiplier during a
 * rising round so the player sees their potential payout live. After the
 * round ends (crash or cash-out) the button briefly flashes the result
 * before flipping back to PLAY for the next round.
 */
export class CrashHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly actionButton: PrimaryButton;
  private readonly statusLabel: Text;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: CrashModel,
    private readonly callbacks: CrashHudCallbacks,
  ) {
    super();
    this.balance = new BalanceDisplay(this.model.balance);
    this.actionButton = new PrimaryButton({
      label: this.idleLabel(),
      width: 220,
      fillActive: 0xff6bd6,
      fillHover: 0x6b3d77,
      textColor: 0xffffff,
      onTap: () => {
        if (this.model.status === 'idle' || this.model.status === 'cashed-out' || this.model.status === 'crashed') {
          this.callbacks.onPlay();
        } else if (this.model.status === 'rising') {
          this.callbacks.onCashOut();
        }
      },
    });
    this.statusLabel = new Text({
      text: 'tap PLAY · cash out before it crashes',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 13,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'center',
      },
    });
    this.statusLabel.anchor.set(0.5);
    this.statusLabel.alpha = 0.7;

    this.addChild(this.balance, this.actionButton, this.statusLabel);

    this.subscriptions.push(
      this.model.events.on('balance-changed', () => {
        this.balance.setAmount(this.model.balance);
      }),
      this.model.events.on('round-started', () => {
        this.actionButton.setLabel(this.cashOutLabel(1));
        this.statusLabel.text = 'cash out before it crashes…';
      }),
      this.model.events.on('multiplier-changed', (event) => {
        if (this.model.status === 'rising') {
          this.actionButton.setLabel(this.cashOutLabel(event.multiplier));
        }
      }),
      this.model.events.on('cashed-out', (event) => {
        this.actionButton.setLabel(`✓ +${event.winnings.toLocaleString()}`);
        this.statusLabel.text = `cashed at ${event.multiplier.toFixed(2)}×`;
      }),
      this.model.events.on('crashed', (event) => {
        if (event.lostBet) {
          this.actionButton.setLabel(`💥 ${event.crashedAt.toFixed(2)}×`);
          this.statusLabel.text = `crashed — bet of ${this.model.bet} lost`;
        } else {
          // Player cashed out — the auxiliary "would have crashed at"
          // info goes in the status label.
          this.statusLabel.text = `cashed safe — would have crashed at ${event.crashedAt.toFixed(2)}×`;
        }
      }),
      this.model.events.on('round-ready', () => {
        // Wait a moment so the player can read the result, then flip back
        // to PLAY for the next round.
        setTimeout(() => {
          if (this.model.status !== 'rising') {
            this.actionButton.setLabel(this.idleLabel());
            this.statusLabel.text = 'tap PLAY for another round';
          }
        }, 1400);
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    const { centerX, machineTop, spinButtonY } = ctx;
    this.balance.x = centerX;
    this.balance.y = Math.max(16, machineTop - 96);
    this.actionButton.x = centerX;
    this.actionButton.y = spinButtonY;
    this.statusLabel.x = centerX;
    this.statusLabel.y = spinButtonY + 56;
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  private idleLabel(): string {
    return `PLAY · ${this.model.bet}`;
  }

  private cashOutLabel(multiplier: number): string {
    return `CASH OUT  ${multiplier.toFixed(2)}×`;
  }
}
